// Words for the sync contract's values (apiv2/dashboard_sync.openapi.yaml). syncContract.test.js checks
// that every refusal code and task state of the contract has words here; an unknown code (a newer
// backend) falls back to the server's own `detail`.
import { errorCode, SyncApiError } from './api';

/** A run's state (0 pending … 5 awaiting approval) in words. */
export const TASK_STATES: Record<number, string> = {
    0: 'Waiting to start',
    1: 'Running',
    2: 'Done',
    3: 'Failed',
    4: 'Cancelled',
    5: 'Waiting for your approval',
};

/** The page's own words for the contract's refusal codes. A code whose server words are already the
 *  client's (they name what to do) is mapped to null: the server's `detail` is shown. */
export const ERROR_CODES: Record<string, string | null> = {
    permission_denied: "You don't have permission to start this sync. Ask an account admin.",
    no_restech: null,
    unknown_integration: 'This booking system is not connected to your account any more. Reload the page.',
    unknown_account: 'This account of your booking system is not connected any more. Reload the page.',
    sync_running: 'This sync is already running. Follow it below.',
    rate_limited: null,
    catalogue_not_loaded: 'The product list is not loaded yet.',
    not_configured: null,
    import_key_flags: null,
    catalogue_sync_disabled: null,
    product_join_invalid: null,
    sync_refused: null,
    sync_busy: 'Our booking-system connections are busy with other syncs right now. Try again in a minute.',
    // The background-task API's refusals.
    bgtask_permission_required: "You don't have permission to do this with this sync. Ask an account admin.",
    bgtask_cancel_ops_only: 'Only our team can cancel this.',
    bgtask_approve_ops_only: 'Only our team can approve this.',
    bgtask_artifacts_ops_only: 'Only our team can download these files.',
    bgtask_result_ops_only: 'Only our team can see this result.',
    bgtask_approve_as_proposed_only: 'This can only be approved as proposed here. Our team can go through it with you.',
    // A guest-list export over MCP (client MCP P2-07): its download is checked again every time, and each refusal
    // says who can change it (review batch 3 L9).
    export_permission_required:
        'Only the person who started this export can download it, and only while they hold the guest-list export permission '
        + 'for all access tags.',
    guest_data_not_enabled: 'Guest data over MCP is turned off for your company, so this export cannot be downloaded. Ask an '
        + 'administrator to turn it on in Settings › AI editing.',
    session_mfa_required: 'Downloading this export needs a session signed in with two-step sign-in (or single sign-on).',
    export_expired: 'This export has expired. Start a new one: ask your AI to export the list again.',
};

/** A booking sync (started or fed on the background-task API) without `ops.sync_bookings`. */
export const BOOKING_SYNC_PERMISSION = 'A booking sync needs the “Ops: sync bookings” permission. Ask an account admin.';

/** What a failed call says to the user. */
export function errorText(e: unknown): string {
    const code = errorCode(e);
    const mapped = code ? ERROR_CODES[code] : null;
    if (mapped) {
        return mapped;
    }
    if (e instanceof SyncApiError) {
        if (code === 'rate_limited' && e.retryAfter) {
            if (e.retryAfter > 3600) {
                // The daily limits (starts per day, products per day) reset at midnight UTC.
                const hours = Math.round(e.retryAfter / 3600);
                return `You've reached today's limit for this. Try again in about ${hours} hour${hours === 1 ? '' : 's'}, or choose fewer products.`;
            }
            const minutes = Math.max(1, Math.round(e.retryAfter / 60));
            return `You've started this several times in the last hour. Try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`;
        }
        return e.message;
    }
    return 'Something went wrong. Try again in a moment.';
}
